// De cijferwidgets boven de Arbeidsmarkt-tab (W16 §6b: widgets bovenaan,
// ontwikkelingen eronder).
//
// Eén regel bepaalt de vorm van deze kaarten: een getal zonder vergelijking is
// geen informatie. "5.450 WW-uitkeringen" zegt niets; "+5,0% waar het land
// +8,3% doet" wel. Elke kaart draagt daarom de landelijke referentielijn, en
// waar het cijfer een voorbehoud heeft staat dat eronder in plaats van in een
// voetnoot die niemand leest.

import { useState } from "react";

import type { OntbrekendBlok, ReeksKaart } from "../contracts";
import type {
  Beweger,
  BronstatusBlok,
  KrapteBlok as KrapteBlokData,
  RegioAfwijking,
  RegioWaarde,
} from "../contracts/api/arbeidsmarkt";
import { color, radius, size, weight } from "../tokens";
import { Sparklijn } from "./ui";

const MAAND = [
  "jan", "feb", "mrt", "apr", "mei", "jun",
  "jul", "aug", "sep", "okt", "nov", "dec",
];

/**
 * "2024-01-01" -> "2024" (jaarreeks), "mei 2026" (maandreeks) of "2026 K1"
 * (kwartaalreeks).
 *
 * De kwartaaltak kwam erbij met de UWV-spanningsindicator: die reeks staat als
 * 1 januari / 1 april / 1 juli / 1 oktober in de database (gemeten), en zonder
 * eigen tak las "2026 K1" op het scherm als "jan 2026" — een maand die de bron
 * niet meet (owner-punt bij de arbeidsmarktronde).
 */
export function periodeLabel(periode: string, soort: string): string {
  const [jaar = periode, maand = ""] = periode.split("-");
  if (soort === "jaar") return jaar;
  if (soort === "kwartaal") {
    const k = Math.floor((Number(maand) - 1) / 3) + 1;
    return k >= 1 && k <= 4 ? `${jaar} K${k}` : jaar;
  }
  const m = MAAND[Number(maand) - 1];
  return m ? `${m} ${jaar}` : jaar;
}

/** Het getal zoals een mens het leest — duizendtallen uitgeschreven. */
export function waardeLabel(waarde: number, eenheid: string): string {
  if (eenheid === "procent") return `${waarde.toLocaleString("nl-NL")}%`;
  if (eenheid === "duizendtal") {
    return Math.round(waarde * 1000).toLocaleString("nl-NL");
  }
  return waarde.toLocaleString("nl-NL");
}

function veranderingLabel(waarde: number, soort: string): string {
  const teken = waarde > 0 ? "+" : "";
  const eenheid = soort === "procentpunt" ? " pp" : "%";
  return `${teken}${waarde.toLocaleString("nl-NL")}${eenheid}`;
}

/**
 * De doortrekking (B6 type 5, feedbackronde 27-08): de gemeten richting van
 * de laatste vier punten, lineair `n` punten doorgetrokken. Alleen bij een
 * reeks van minstens acht metingen — minder is te dun om een richting te
 * noemen. Dit is nadrukkelijk GEEN voorspelling van het platform (de grens
 * uit ADR 2026-08-16): het scherm zegt dat er hard bij, en de band is
 * gestippeld — zelfde idioom als het speculatiekader in de chat.
 */
export function doortrekken(
  punten: readonly { waarde: number }[],
  n = 3,
): number[] {
  if (punten.length < 8) return [];
  const staart = punten.slice(-4).map((p) => p.waarde);
  const stappen = staart.slice(1).map((v, i) => v - staart[i]!);
  const richting = stappen.reduce((s, v) => s + v, 0) / stappen.length;
  const uit: number[] = [];
  let vorige = staart[staart.length - 1]!;
  for (let i = 0; i < n; i++) {
    vorige += richting;
    uit.push(Math.round(vorige * 100) / 100);
  }
  return uit;
}

/** Sparkline: de gedeelde Sparklijn uit ui.tsx (poetsronde 28-08 — er
 *  bestonden drie implementaties naast elkaar), op de maat van dit scherm. */
function Lijn({
  punten,
  doortrekking = [],
}: {
  punten: readonly { waarde: number }[];
  doortrekking?: readonly number[];
}) {
  return (
    <Sparklijn
      waarden={punten.map((p) => p.waarde)}
      doortrekking={doortrekking}
      w={190}
      h={34}
      style={{ marginTop: 9 }}
    />
  );
}

export function ReeksWidget({
  kaart,
  onOpen,
}: {
  kaart: ReeksKaart;
  onOpen: () => void;
}) {
  const laatste = kaart.laatste;
  const soort = kaart.veranderingSoort ?? "procent";
  // Bij WW en werkloosheid is stijgen slecht; bij banen en lonen juist goed.
  // Dat oordeel hoort niet in een kleurtje dat de code zelf verzint, dus de
  // kaart kleurt alleen de RICHTING neutraal-donker en laat de duiding aan de
  // tekst. Eén uitzondering: de referentielijn, want die vergelijkt.
  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        textAlign: "left",
        background: color.surface,
        border: `1px solid ${color.line}`,
        borderRadius: radius.card,
        padding: "14px 16px",
        cursor: "pointer",
        font: "inherit",
        display: "block",
        width: "100%",
      }}
    >
      <div
        style={{
          fontSize: size.label,
          fontWeight: weight.medium,
          color: color.faint,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        {kaart.titel}
      </div>
      <div
        style={{
          fontSize: 25,
          fontWeight: weight.bold,
          letterSpacing: "-0.02em",
          marginTop: 5,
          fontVariantNumeric: "tabular-nums",
          color: color.ink,
        }}
      >
        {laatste ? waardeLabel(laatste.waarde, kaart.eenheid) : "—"}
      </div>
      <div style={{ fontSize: 12.5, color: color.soft, marginTop: 2 }}>
        {laatste ? periodeLabel(laatste.periode, kaart.periodeSoort) : "geen meting"}
        {kaart.verandering !== null && kaart.verandering !== undefined && (
          <>
            {" · "}
            <b style={{ color: color.ink, fontWeight: weight.medium }}>
              {veranderingLabel(kaart.verandering, soort)}
            </b>
            {" t.o.v. een jaar eerder"}
          </>
        )}
      </div>
      {kaart.referentieVerandering !== null && kaart.referentieVerandering !== undefined && (
        <div style={{ fontSize: 12.5, color: color.faint, marginTop: 1 }}>
          Nederland: {veranderingLabel(kaart.referentieVerandering, soort)}
        </div>
      )}
      <Lijn
        punten={kaart.punten ?? []}
        doortrekking={doortrekken(kaart.punten ?? [])}
      />
      <div
        style={{
          fontSize: 11.5,
          color: color.faint,
          marginTop: 8,
          borderTop: `1px solid ${color.lineSoft}`,
          paddingTop: 7,
        }}
      >
        {kaart.regio.naam}
        {kaart.voorbehoud ? ` — ${kaart.voorbehoud}` : ""}
        {doortrekken(kaart.punten ?? []).length > 0 &&
          " — gestippeld: de gemeten richting doorgetrokken, geen voorspelling"}
      </div>
    </button>
  );
}

/** Een blok dat niet gevuld kan worden, mét de reden. W8 §e. */
export function OntbreektWidget({ blok }: { blok: OntbrekendBlok }) {
  return (
    <div
      style={{
        background: color.surfaceAlt,
        border: `1px dashed ${color.border}`,
        borderRadius: radius.card,
        padding: "14px 16px",
      }}
    >
      <div
        style={{
          fontSize: size.label,
          fontWeight: weight.medium,
          color: color.faint,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        {blok.titel}
      </div>
      <div style={{ fontSize: 15, color: color.faint, fontWeight: weight.medium, marginTop: 6 }}>
        Binnenkort
      </div>
      <div
        style={{
          fontSize: 11.5,
          color: color.faint,
          marginTop: 8,
          borderTop: `1px solid ${color.lineSoft}`,
          paddingTop: 7,
          lineHeight: 1.5,
        }}
      >
        {blok.reden}
        {blok.wachtOp ? <> <b style={{ fontWeight: weight.medium }}>Wacht op:</b> {blok.wachtOp}</> : null}
      </div>
    </div>
  );
}

/**
 * Bouwrun baan C (C3): de verouderd-melding boven de bestaande widgets. Komt
 * uit `bronstatus` van /arbeidsmarkt/overzicht (de maandstap `cijfers` of,
 * zonder ophaalbestand, de jongste periode per bron in de database). Alles ok
 * = geen melding; de widgets zelf blijven ongewijzigd.
 */
export function VerouderdMelding({ status }: { status?: BronstatusBlok | null }) {
  const mis = (status?.bronnen ?? []).filter((b) => b.status !== "ok");
  if (mis.length === 0) return null;
  return (
    <div
      role="status"
      data-testid="verouderd-melding"
      style={{
        marginTop: 12,
        padding: "10px 14px",
        borderRadius: radius.block,
        background: color.accentAmberBg,
        border: `1px solid ${color.accentAmber}`,
        color: color.body,
        fontSize: size.meta,
        lineHeight: 1.5,
      }}
    >
      {mis.map((b) => {
        const wie = `Cijfers van ${b.aanbieder ?? b.bron}${b.titel ? ` (${b.titel})` : ""}`;
        const zin =
          b.status === "te oud" && b.jongstePeriode
            ? `${wie} zijn niet actueel.`
            : `${wie} konden bij de laatste ophaal niet worden opgehaald${b.fout ? `: ${b.fout}` : "."}`;
        return (
          <div key={b.bron} style={{ overflowWrap: "anywhere" }}>
            <span style={{ fontWeight: weight.medium, color: color.accentAmber }}>Let op. </span>
            {zin}
          </div>
        );
      })}
    </div>
  );
}

// ── Bouwrun baan C (C4): drie blokken onder de bestaande widgets ────────────
// Krapte per propositie, "Waar wijkt onze regio af" en "Grootste beweger".
// Alle drie lezen de UWV-spanningsindicator (vacatures per werkzoekende). De
// bestaande widgets hierboven blijven zoals ze zijn.

const blokKop = {
  fontSize: size.label,
  fontWeight: weight.medium,
  color: color.faint,
  textTransform: "uppercase" as const,
  letterSpacing: "0.06em",
};

const blokKaart = {
  background: color.surface,
  border: `1px solid ${color.line}`,
  borderRadius: radius.card,
  padding: "14px 16px",
  minWidth: 0,
};

function spanningLabel(w: number | null | undefined): string {
  return w === null || w === undefined
    ? "—"
    : w.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function WaardenRij({ waarden }: { waarden: readonly RegioWaarde[] }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "2px 14px", fontSize: 12.5, color: color.soft }}>
      {waarden.map((w, i) => (
        <span key={w.code} style={{ fontVariantNumeric: "tabular-nums" }}>
          {w.naam.replace(/ \((AM|PV)\)$/, "")}:{" "}
          <b style={{ color: i === 0 ? color.ink : color.soft, fontWeight: weight.medium }}>
            {spanningLabel(w.waarde)}
          </b>
        </span>
      ))}
    </div>
  );
}

/** Eerst zoveel beroepen per propositie, daarna "toon alle (n)" (baan C2, K11):
 *  een unie van sectoren geeft 10-30 beroepen, en een lange lijst leest niet. */
export const KRAPTE_EERST = 8;

type KrapteBeroepData = KrapteBlokData["proposities"][number]["beroepen"][number];

/** Hoogste spanning in onze regio eerst; beroepen zonder cijfers onderaan
 *  (stabiel: verder de volgorde van de server). */
export function sorteerKrapte(beroepen: readonly KrapteBeroepData[]): KrapteBeroepData[] {
  const hier = (b: KrapteBeroepData) => b.waarden[0]?.waarde ?? null;
  return beroepen
    .map((b, i) => ({ b, i }))
    .sort((x, y) => {
      const a = hier(x.b);
      const c = hier(y.b);
      if (a === null && c === null) return x.i - y.i;
      if (a === null) return 1;
      if (c === null) return -1;
      return c - a || x.i - y.i;
    })
    .map((x) => x.b);
}

function koppelingRegel(blok: KrapteBlokData): string | null {
  const k = blok.koppeling;
  if (k && k.stand === "aangepast") {
    const op = k.op ? new Date(k.op) : null;
    const wanneer =
      op && !Number.isNaN(op.getTime())
        ? ` op ${op.toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" })}`
        : "";
    return `${blok.melding ?? ""} Aangepast${k.door ? ` door ${k.door}` : ""}${wanneer}.`.trim();
  }
  return blok.melding ?? null;
}

function KraptePropositie({ p }: { p: KrapteBlokData["proposities"][number] }) {
  const [alles, setAlles] = useState(false);
  const gesorteerd = sorteerKrapte(p.beroepen);
  const zichtbaar = alles ? gesorteerd : gesorteerd.slice(0, KRAPTE_EERST);
  return (
    <div data-testid={`krapte-${p.propositie}`} style={{ marginTop: 12 }}>
      <div style={{ fontSize: 13.5, fontWeight: weight.medium, color: color.ink }}>
        {p.naam}
        {p.eigen && (
          <span style={{ fontSize: 11.5, color: color.faint, fontWeight: weight.medium }}>
            {" "}· jouw propositie
          </span>
        )}
      </div>
      {zichtbaar.map((b) => (
        <div
          key={b.sleutel}
          data-beroep={b.sleutel}
          style={{ borderTop: `1px solid ${color.lineSoft}`, padding: "7px 0 5px", marginTop: 5 }}
        >
          <div style={{ fontSize: 13, color: color.body, overflowWrap: "anywhere" }}>
            {b.naam}
            {b.periode && (
              <span style={{ color: color.faint }}> · {periodeLabel(b.periode, "kwartaal")}</span>
            )}
            {b.verandering !== null && b.verandering !== undefined && (
              <span style={{ color: color.faint }}>
                {" · "}
                {veranderingLabel(b.verandering, b.veranderingSoort ?? "procent")} t.o.v. een jaar eerder
              </span>
            )}
          </div>
          {b.metCijfers === false ? (
            <div style={{ fontSize: 12.5, color: color.faint }}>nog geen cijfers</div>
          ) : (
            <WaardenRij waarden={b.waarden} />
          )}
          {b.sectoren && b.sectoren.length > 0 && (
            <div style={{ fontSize: 11.5, color: color.faint, marginTop: 2, overflowWrap: "anywhere" }}>
              {b.sectoren.map((s) => s.naam).join(" · ")}
            </div>
          )}
          {b.voorbehoud && (
            <div style={{ fontSize: 11.5, color: color.faint, marginTop: 3, lineHeight: 1.5 }}>
              {b.voorbehoud}
            </div>
          )}
        </div>
      ))}
      {gesorteerd.length > KRAPTE_EERST && (
        <button
          type="button"
          onClick={() => setAlles((a) => !a)}
          style={{
            marginTop: 6,
            background: "none",
            border: "none",
            padding: 0,
            fontSize: 12.5,
            color: color.ink,
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          {alles ? "toon minder" : `toon alle (${gesorteerd.length})`}
        </button>
      )}
    </div>
  );
}

/** Krapte per pilotpropositie; de eigen propositie staat vooraan. Baan C2: de
 *  beroepen komen uit de sectoren van de propositie (best guess, aan te passen
 *  in Beheer > Configuratie > Beroepen). */
export function KrapteBlok({ blok }: { blok?: KrapteBlokData | null }) {
  if (!blok) return null;
  const regel = koppelingRegel(blok);
  const metBeroepen = blok.proposities.filter((p) => p.beroepen.length > 0);
  return (
    <div data-testid="krapte-blok" style={blokKaart}>
      <div style={blokKop}>Krapte per propositie</div>
      <div style={{ fontSize: 12.5, color: color.faint, marginTop: 4, lineHeight: 1.5 }}>
        UWV-spanningsindicator: vacatures per werkzoekende. Boven 1 is er meer werk dan mensen.
      </div>
      {regel && (
        <div
          data-testid="krapte-koppeling"
          style={{ fontSize: 12, color: color.faint, marginTop: 6, lineHeight: 1.5 }}
        >
          {regel}
        </div>
      )}
      {metBeroepen.map((p) => (
        <KraptePropositie key={p.propositie} p={p} />
      ))}
    </div>
  );
}

/** Waar wijkt onze regio af: grootste verschil met de landelijke lijn eerst. */
export function RegioAfwijkingBlok({ blok }: { blok?: RegioAfwijking | null }) {
  if (!blok) return null;
  return (
    <div data-testid="regio-afwijking-blok" style={blokKaart}>
      <div style={blokKop}>Waar wijkt onze regio af</div>
      <div style={{ fontSize: 12.5, color: color.faint, marginTop: 4, lineHeight: 1.5 }}>
        Krapte in {blok.regio.naam.replace(/ \((AM|PV)\)$/, "")} tegen {blok.vergelijkMet.naam}, per beroep
        {blok.propositie ? ` van ${blok.propositie.naam}` : ""}.
      </div>
      {blok.items.length === 0 ? (
        <div style={{ fontSize: 13, color: color.faint, marginTop: 10 }}>Nog geen cijfers om te vergelijken.</div>
      ) : (
        blok.items.map((i) => (
          <div
            key={i.sleutel}
            style={{ borderTop: `1px solid ${color.lineSoft}`, padding: "7px 0 5px", marginTop: 5 }}
          >
            <div style={{ fontSize: 13, color: color.body, overflowWrap: "anywhere" }}>
              {i.naam}
              <span style={{ color: color.faint }}>
                {" · "}
                <b style={{ color: color.ink, fontWeight: weight.medium }}>
                  {i.verschil > 0 ? "+" : ""}
                  {spanningLabel(i.verschil)}
                </b>{" "}
                {i.verschil >= 0 ? "krapper" : "ruimer"} dan {blok.vergelijkMet.naam} ·{" "}
                {periodeLabel(i.periode, "kwartaal")}
              </span>
            </div>
            <WaardenRij waarden={i.waarden} />
          </div>
        ))
      )}
    </div>
  );
}

/** Het beroep dat het meest veranderde tegen een jaar eerder. */
export function BewegerBlok({ blok }: { blok?: Beweger | null }) {
  return (
    <div data-testid="beweger-blok" style={blokKaart}>
      <div style={blokKop}>Grootste beweger</div>
      {!blok ? (
        <div style={{ fontSize: 13, color: color.faint, marginTop: 10 }}>
          Nog geen beroep met een meting van een jaar eerder.
        </div>
      ) : (
        <>
          <div style={{ fontSize: 15, fontWeight: weight.medium, color: color.ink, marginTop: 6, overflowWrap: "anywhere" }}>
            {blok.naam}
          </div>
          <div style={{ fontSize: 25, fontWeight: weight.bold, letterSpacing: "-0.02em", marginTop: 4, color: color.ink, fontVariantNumeric: "tabular-nums" }}>
            {veranderingLabel(blok.verandering, blok.veranderingSoort)}
          </div>
          <div style={{ fontSize: 12.5, color: color.soft, marginTop: 2 }}>
            {spanningLabel(blok.vorigeWaarde)} in {periodeLabel(blok.vorigePeriode, "kwartaal")} →{" "}
            {spanningLabel(blok.waarde)} in {periodeLabel(blok.periode, "kwartaal")}
          </div>
          <div style={{ fontSize: 11.5, color: color.faint, marginTop: 8, borderTop: `1px solid ${color.lineSoft}`, paddingTop: 7 }}>
            {blok.regio.naam} — vergeleken met een jaar eerder
            {blok.propositie ? ` · beroepen van ${blok.propositie.naam}` : ""}
          </div>
        </>
      )}
    </div>
  );
}
